import { MediaMatcher } from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { IApiKey, ICliente, IFilter, IListado, IQueryParam } from 'modelos/src';

import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../auxiliares/dialog/dialog.service';
import { take } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../auxiliares/filtro-tabla/filtro-tabla/filtro-tabla.component';
import { ApiKeysService } from '../apikeys.service';
import { CrearEditarApiKeyComponent } from '../crear-editar-apikeys/crear-editar-apikeys.component';

@Component({
  selector: 'app-listar-apikeys',
  templateUrl: './listar-apikeys.component.html',
  styleUrls: ['./listar-apikeys.component.scss'],
  standalone: false,
})
export class ListarApiKeysComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private media = inject(MediaMatcher);
  private listadosService = inject(ListadosService);
  helper = inject(HelperService);
  service = inject(ApiKeysService);
  private dialogService = inject(DialogService);
  matDialog = inject(MatDialog);

  public search: IRegExpSearch = {
    fields: ['nombre', 'dni', 'email', 'telefono'],
  };
  public clientes: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'select',
  };

  public loading = true;
  public columnas = [
    'apiName',
    'fechaCreacion',
    'fechaExpiracion',
    'key',
    'roles',
    'cliente',
    'acciones',
  ];
  public nombreColumnas = [
    'Nombre de la API',
    'Fecha de Creación',
    'Fecha de Expiración',
    'Key',
    'Roles',
    'Cliente',
    'Acciones',
  ];
  public saveColumnas = 'tabla-apikey';
  public dataSource = new MatTableDataSource<IApiKey>([]);
  // FILTRO BUSQUEDA Y PAGINACION
  public totalCount = 0;
  public clientes$?: Subscription;
  public clientesDatos?: ICliente[];
  private initialFilters: IFilter<IApiKey> = {};

  public queryParams: IQueryParam = {
    page: 0,
    limit: +this.helper.pageSize,
    sort: 'fechaCreacion',
    filter: JSON.stringify(this.initialFilters),
    select: '',
  };
  // Mobile query
  public mobileQuery: MediaQueryList;
  //
  // Listado Continuo
  private datos$?: Subscription;

  constructor() {
    this.mobileQuery = this.media.matchMedia('(max-width: 599px)');
    this.mobileQuery.addEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
  }

  // ################################################################################
  // ################################# TABLA 1 ######################################
  // ################################################################################

  public pageEvent(event: PageEvent): void {
    this.helper.pageEvent(event);
    this.queryParams.page = event.pageIndex;
    this.queryParams.limit = event.pageSize;
    this.actualizar();
  }
  public async cambioFiltro(filters: IFilter<any>): Promise<void> {
    this.loading = true;
    this.queryParams = {
      page: 0,
      limit: this.queryParams.limit,
      sort: this.queryParams.sort,
      populate: this.queryParams.populate,
      select: this.queryParams.select,
      filter: JSON.stringify(filters),
    };
    await this.actualizar();
    this.loading = false;
  }
  public sortChange(event: Sort): void {
    this.queryParams.sort =
      event.direction === 'asc' ? event.active : `-${event.active}`;
    this.actualizar();
  }

  //

  public async eliminar(dato: IApiKey): Promise<void> {
    const confirm = await this.dialogService.confirm(
      'Confirme la acción',
      `¿Eliminar la Api Key?`,
    );
    if (confirm) {
      try {
        this.loading = true;
        await this.service.eliminar(dato._id!).pipe(take(1)).toPromise();
        this.helper.notifSuccess('Eliminación correcta');
        this.loading = false;
      } catch (error) {
        console.error(error);
        this.helper.notifError(error);
        this.loading = false;
      }
    }
  }

  // Listar
  public async actualizar(): Promise<void> {
    this.loading = true;
    await this.listar();
    this.loading = false;
  }

  private async listar(): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = this.listadosService
      .subscribe<IListado<IApiKey>>('apikeys', this.queryParams)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.dataSource.data = data.datos;
        console.debug(new Date().toLocaleString(), `listado de apikeys`, data);
      });
    await this.listadosService.getLastValue('apikeys', this.queryParams);
  }

  public async crear(): Promise<void> {
    const config: MatDialogConfig = {
      width: '700px',
    };
    this.matDialog.open(CrearEditarApiKeyComponent, config);
  }

  public async editar(data: IApiKey): Promise<void> {
    const config: MatDialogConfig = {
      data,
      width: '700px',
    };
    this.matDialog.open(CrearEditarApiKeyComponent, config);
  }

  public copyToClip(t: string) {
    navigator.clipboard.writeText(t);
  }

  private async listarClientes(): Promise<void> {
    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', this.queryParams)
      .subscribe((data) => {
        this.clientesDatos = data.datos;
        console.debug(new Date().toLocaleString(), `listado de clientes`, data);
      });
    await this.listadosService.getLastValue('clientes', this.queryParams);
  }

  public clienteNombre(id: string) {
    const cliente = this.clientesDatos?.find((c) => c._id === id);
    return cliente?.nombre;
  }

  //

  async ngOnInit(): Promise<void> {
    await Promise.all([this.actualizar(), this.listarClientes()]);
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
    this.datos$?.unsubscribe();
    this.clientes$?.unsubscribe();
  }
}
